import { PropTypes } from 'prop-types';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect, useDispatch, useSelector } from 'react-redux';
import AccionaButton from '../../../../../components/AccionaButton';
import AccionaTable from '../../../../../components/AccionaTable';
import AccionaTableButton from '../../../../../components/AccionaTable/AccionaTableButton';
import Modal from '../../../../../components/Modal';
import RemoveModal from '../../../../../components/RemoveModal';
import { MainContext } from '../../../../../context/MainContext';
import WithBackButton from '../../../../../layout/WithBackButton';
import {
  clearEdited,
  clearErrors,
  deleteDevices,
  fetchDevices,
  getDevices,
  updateDevice,
} from '../../../../../services/redux/thermalControl/devices/actions';
import CreateDevice from '../Create';
import { COLUMNS_HEADERS } from './columns';
import styles from './index.module.scss';
import { NotificationsContext } from '../../../../../context/NotificationsContext';
import BatteryStatus from '../../../../../components/BatteryStatus';
import { useParams } from 'react-router-dom';

const { actionsWrapper, availability } = styles;

const List = ({ getItems, deleteItems }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const id = useParams();
  const { setLoading } = useContext(MainContext);
  const { pushNotification } = useContext(NotificationsContext);
  const { isEdited, errorMessages, fetchedNewDevices } = useSelector(
    ({ thermalControlDevices }) => thermalControlDevices,
  );
  const [selectedRows, setSelectedRows] = useState([]);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deviceForEdit, setDeviceForEdit] = useState();
  const [pagination, setPagination] = useState();

  const onSingleDeleteButtonClick = (device) => {
    setSelectedRows([device]);
    setShowRemoveModal(true);
  };

  const handleOnBulkDeleting = () => {
    setShowRemoveModal(false);
    deleteItems(selectedRows);
    setSelectedRows([]);
  };

  const editDevice = (device) => {
    setLoading(true);
    dispatch(updateDevice(device));
  };

  useEffect(() => {
    if (isEdited) {
      setLoading(false);
      setDeviceForEdit(undefined);
      setShowEditModal(false);
      dispatch(clearEdited());
    }
  }, [isEdited, setLoading, dispatch]);

  useEffect(() => {
    dispatch(clearErrors());
  }, [dispatch, showEditModal]);

  useEffect(() => {
    if (errorMessages) {
      const { status } = errorMessages.response;
      pushNotification({
        message: intl.formatMessage({
          id: `${
            status === 403
              ? 'strength.thermalcontrol.devices.delete.403.error'
              : 'messages.default'
          }`,
        }),
        type: 'error',
      });
    }
  }, [errorMessages, intl, pushNotification]);

  useEffect(() => {
    if (fetchedNewDevices !== null) {
      setLoading(false);
      pushNotification({
        message: intl.formatMessage(
          { id: `strength.thermalcontrol.devices.fetched` },
          { fetched: fetchedNewDevices },
        ),
        type: fetchedNewDevices === 0 ? 'error' : 'success',
      });
    }
  }, [fetchedNewDevices, intl, pushNotification, setLoading]);

  const handleOnChange = useCallback(
    ({ pagination, filters }) => {
      setPagination({
        ...pagination,
        filters: { pid: id, ...filters },
      });
    },
    [id],
  );

  return (
    <>
      <div className={`row ${actionsWrapper}`}>
        <div className="col-sm-12">
          <AccionaButton
            id="addBtn"
            onClick={() => {
              setLoading(true);
              dispatch(fetchDevices(pagination));
            }}
            msg={intl.formatMessage({ id: 'button.add' })}
            classIcon="icon fas fa-sync"
            type="white"
          />
        </div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <AccionaTable
            columns={COLUMNS_HEADERS}
            getItems={getItems}
            onChange={handleOnChange}
            entity="thermalControlDevices"
            dataParseFunction={(data) =>
              data.map((d) => ({
                ...d,
                reference_number: (
                  <span
                    style={{
                      display: 'flex',
                      fontFamily: '15px',
                      alignItems: 'center',
                      whiteSpace: 'pre',
                    }}
                  >
                    {d.reference_number} {'  '}
                    <BatteryStatus battery={d.batt} />
                  </span>
                ),
                project: d.project
                  ? `${d.project.work_code} ${d.project.name}`
                  : '--',
                location: d.location
                  ? intl.formatMessage({ id: d.location })
                  : '--',
                inUse: (
                  <div className={availability}>
                    {d.inUse ? (
                      <>
                        {intl.formatMessage({ id: 'no' })}{' '}
                        <i className="iconRed fas fa-times-circle fa-lg"></i>
                      </>
                    ) : (
                      <>
                        {intl.formatMessage({ id: 'yes' })}{' '}
                        <i className="iconGreen fas fa-check-circle fa-lg"></i>
                      </>
                    )}
                  </div>
                ),
                rawObject: d,
              }))
            }
            actions={(device) => (
              <>
                <AccionaTableButton
                  onClick={() => {
                    setShowEditModal(true);
                    setDeviceForEdit(device.rawObject);
                  }}
                  type="edit"
                  toolTip={intl.formatMessage({
                    id: 'editAction.devices',
                  })}
                />
                <AccionaTableButton
                  onClick={() => {
                    onSingleDeleteButtonClick(device.rawObject);
                  }}
                  type="delete"
                  toolTip={intl.formatMessage({
                    id: 'deleteAction.devices',
                  })}
                />
              </>
            )}
          />
        </div>
      </div>

      <RemoveModal
        entity="devices"
        selectedRows={selectedRows}
        msg={intl.formatMessage(
          { id: 'delete.devices' },
          { total: selectedRows.length },
        )}
        onOk={handleOnBulkDeleting}
        onCancel={() => setShowRemoveModal((prev) => !prev)}
        hidden={!showRemoveModal}
      />
      {deviceForEdit && (
        <Modal
          title={intl
            .formatMessage({
              id: 'strength.thermalcontrol.devices.list.modal.edit.title',
            })
            .toUpperCase()}
          open={showEditModal}
          onCancel={() => {
            setShowEditModal(false);
          }}
          closable
          width="50vw"
        >
          <CreateDevice
            initialData={deviceForEdit}
            onFinish={editDevice}
            onCancel={() => {
              setShowEditModal(false);
              setDeviceForEdit(undefined);
            }}
          />
        </Modal>
      )}
    </>
  );
};

List.propTypes = {
  getItems: PropTypes.func.isRequired,
  deleteItems: PropTypes.func.isRequired,
};

export default connect(() => ({}), {
  getItems: getDevices,
  deleteItems: deleteDevices,
})(WithBackButton(List));
